/**
 * Convert date array to formatted string
 */
const formatDateFromArray = (dateArray) => {
  if (!dateArray || !Array.isArray(dateArray)) return 'N/A'
  const [year, month, day, hour, minute, second] = dateArray
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Get document info from document ID
 */
const getDocumentInfo = (documentId, documentType) => {
  const lawNames = {
    'gcc-common-vat-agreement': 'GCC Common VAT Agreement',
    'gcc-common-customs-agreement': 'GCC Common Customs Agreement',
    'gcc-common-excise-agreement': 'GCC Common Excise Agreement',
    'uae-cit-fdl-47-of-2022': 'CIT (FDL No 47 of 2022, as amended)',
    'uae-dmtt-fdl-60-of-2023': 'DMTT (FDL No 60 of 2023)',
    'uae-indirecttax-law-vat-uaevat-8-of-2017-as-amended':
      'VAT (FDL No 8 of 2017, as amended)',
    'uae-tp-fdl-28-of-2022-as-amended':
      'Tax Procedures (FDL No 28 of 2022, as amended)',
    'uae-indirect-excise-7-of-2017-as-amended':
      'Excise Tax (FDL No 7 of 2017, as amended)',
    'uae-fatca-of-2024': 'FATCA (Foreign Account Tax Compliance Act) 2024',
    'uae-crs-federal-decree-48-of-2018':
      'CRS (Common Reporting Standard, Federal Decree No 48 of 2018)',
    'ksa-income-tax-law-of-2004': 'Income Tax Law 2004',
    'ksa-vat-law-m-113-of-2017': 'VAT Law 2017',
    'ksa-customs-law': 'Customs Law',
    'ksa-fatca-of-2017': 'FATCA (Foreign Account Tax Compliance Act) 2017',
    'ksa-excise-law-of-2019': 'Excise Law 2019',
    'ksa-zakat-law-of-1951': 'Zakat Collection Law 1951',
    'kuwait-draft-business-profit-tax-law-of-2024':
      'Draft Business Profit Tax Law 2024',
    'kuwait-kit-law-no-2-of-2008-amending-decree-no-3-of-1955':
      'Income Tax Law - Decree No. 3 of 1955',
    'kuwait-qdmtt-decree-law-157-of-2024': 'QDMTT Law - Decree No. 157 of 2024',
    'qatar-income-tax-law-24-of-2018': 'Income Tax Law 24 of 2018',
    'qatar-dmtt-fdl-22-of-2024': 'DMTT (FDL No 22 of 2024)',
    'qatar-tax-procedures-law': 'Tax Procedures Law',
    'qatar-excise-law-25-of-2018': 'Excise Law 2018',
    'qatar-fatca-of-2014-iga-m1':
      'FATCA (Foreign Account Tax Compliance Act) 2014',
    'oman-income-tax-law-royal-decree-28-of-2009':
      'Income Tax Law (Royal Decree No 28 of 2009, as amended)',
    'oman-vat-law-royal-decree-121-of-2020':
      'VAT Law - Royal Decree No. 121 of 2020',
    'oman-excise-tax-law-royal-decree-23-of-2019':
      'Excise Tax Law (Royal Decree No 23 of 2019)',
    'oman-fatca-of-2014-direct':
      'FATCA (Foreign Account Tax Compliance Act) 2014',
    'oman-cbcr-law': 'Country-by-Country Reporting Law',
    'bahrain-dmtt-law-11-of-2024':
      'Bahrain DMTT Law (Decree Law No. 11 of 2024)',
    'bahrain-vat-law-48-of-2018': 'Bahrain VAT Law 48 of 2018',
  }

  const countryMap = {
    uae: 'UAE',
    ksa: 'KSA',
    kuwait: 'Kuwait',
    qatar: 'Qatar',
    oman: 'Oman',
    bahrain: 'Bahrain',
  }

  const firstPart = documentId.split('-')[0].toLowerCase()
  const country = countryMap[firstPart] || 'N/A'

  let lawName = lawNames[documentId] || documentId
  let docNumber = null

  if (documentType === 'article') {
    const parts = documentId.split('-article-')
    lawName = lawNames[parts[0]] || parts[0]
    docNumber = parts[1] ? `Article ${parts[1]}` : null
  } else if (documentType === 'decision') {
    const match = documentId.match(
      /^(.+?)-(cd|md|fta|ad|zd|td|bl|er|ers|ir|eb|jd)-(.+)$/i,
    )
    if (match) {
      lawName = lawNames[match[1]] || match[1]
      docNumber = `${match[2].toUpperCase()} ${match[3]}`
    }
  } else if (documentType === 'guide' || documentType === 'guideline') {
    const match = documentId.match(
      /^(.+?)-(?:tpgpc|pc|guide|circular|manual)-(.+)$/i,
    )
    if (match) {
      lawName = lawNames[match[1]] || match[1]
      docNumber = `${match[2].toUpperCase()} ${match[3] || ''}`
    }
  }

  return { country, lawName, docNumber }
}

/**
 * Transform annotation data for export
 */
const transformAnnotationForExport = (annotation) => {
  const { country, lawName, docNumber } = getDocumentInfo(
    annotation.documentId,
    annotation.documentType,
  )

  const isQuickHighlight = annotation.annotationContent === 'Quick Highlight'

  return {
    Country: country,
    'Law/Regulation': lawName,
    'Document Type':
      annotation.documentType?.charAt(0).toUpperCase() +
        annotation.documentType?.slice(1) || 'N/A',
    'Document Number': docNumber || 'N/A',
    'Selected Text': annotation.selectedText || '',
    'Annotation Note':
      isQuickHighlight || !annotation.annotationContent
        ? 'No note added by user'
        : annotation.annotationContent,
    Category: annotation.category || 'general',
    Important: annotation.isImportant ? 'Yes' : 'No',
    Tags:
      annotation.tags && annotation.tags.length > 0
        ? annotation.tags.join(', ')
        : 'No tags added by user',
    'Page URL': annotation.pageUrl || '',
    'Created Date': formatDateFromArray(annotation.createdAt),
    'Updated Date': formatDateFromArray(annotation.updatedAt),
  }
}

/**
 * Export annotations to CSV
 */
export const exportAnnotationsToCSV = (
  annotations,
  filename = 'gtl_annotations',
) => {
  if (!annotations || annotations.length === 0) {
    throw new Error('No annotations to export')
  }

  const transformedData = annotations.map(transformAnnotationForExport)

  // Create CSV content
  const headers = Object.keys(transformedData[0])
  const csvContent = [
    headers.join(','),
    ...transformedData.map((row) =>
      headers
        .map((header) => {
          const value = row[header]
          // Escape quotes and wrap in quotes if contains comma, newline, or quote
          const stringValue = String(value).replace(/"/g, '""')
          return /[",\n]/.test(stringValue) ? `"${stringValue}"` : stringValue
        })
        .join(','),
    ),
  ].join('\n')

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute(
    'download',
    `${filename}_${new Date().toISOString().split('T')[0]}.csv`,
  )
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}

/**
 * Export selected annotations from all pages
 */
export const exportSelectedAnnotations = async (
  apiService,
  selectedIds,
  filters = {},
  searchQuery = '',
  options = {},
) => {
  try {
    if (!selectedIds || selectedIds.size === 0) {
      throw new Error('No annotations selected')
    }

    const {
      fetchAnnotations = null,
      filename = 'gtl_annotations',
    } = options

    // Fetch all annotations (use a large page size to get everything)
    const searchParams = {
      query: searchQuery.trim() || null,
      category: filters.category !== 'all' ? filters.category : null,
      priority: filters.priority !== 'all' ? filters.priority : null,
      isImportant:
        filters.isImportant !== 'all' ? filters.isImportant === 'true' : null,
      tags: filters.tags && filters.tags.length > 0 ? filters.tags : null,
      page: 0,
      size: 10000, // Large number to get all annotations
      sortBy: 'createdAt',
      sortDir: 'desc',
    }

    const response = fetchAnnotations
      ? await fetchAnnotations(searchParams)
      : await apiService.searchUserAnnotations(searchParams)
    const allAnnotations = Array.isArray(response)
      ? response
      : response?.content || []

    // Filter only selected annotations
    const selectedAnnotations = allAnnotations.filter((annotation) =>
      selectedIds.has(annotation.id),
    )

    if (selectedAnnotations.length === 0) {
      throw new Error('No matching annotations found')
    }

    // Export to CSV
    exportAnnotationsToCSV(selectedAnnotations, filename)

    return selectedAnnotations.length
  } catch (error) {
    console.error('Export error:', error)
    throw error
  }
}
